import { useCallback, useRef, useState, type ReactNode } from "react";
import { Button, Modal, Radio } from "./components";
import { money } from "./format";

/**
 * Variante bei manueller Zusage (Antoine, 26.10.2026): „MAESTRO soll das zukünftig immer manuell
 * abfragen.“ Anlass V-1228: zwei aktive Varianten, der Betreiber setzte „Zugesagt“ und trug eine
 * Zahlung ein, ohne dass je eine Variante gewählt war — der Auftragsbetrag blieb beim höchsten
 * Angebot.
 *
 * Der Server antwortet in dem Fall mit 422 `variante_waehlen` und der Liste der Varianten. Dieser
 * Dialog fragt „Welche Variante hat der Gast gebucht?“; danach wird dieselbe Aktion mit
 * `chosenOptionId` wiederholt (`useVarianteWahl().mitVarianteWahl`).
 */

export interface VarianteOption {
  id: string;
  titel: string;
  betragCents: number;
  gaeste: number;
}

/** Die Variantenliste, falls der Fehler die Server-Rückfrage ist; sonst `null`. */
export function variantenAusFehler(err: unknown): VarianteOption[] | null {
  const e = err as { code?: string; body?: { varianten?: unknown } } | null;
  if (!e || (e.code !== "variante_waehlen" && e.code !== "variante_ungueltig")) return null;
  const liste = e.body?.varianten;
  return Array.isArray(liste) && liste.length > 0 ? (liste as VarianteOption[]) : null;
}

/** Preis je Gast, nur zur Anzeige — der Betrag für die Buchung kommt immer vom Server. */
function proGast(v: VarianteOption): string | null {
  return v.gaeste > 0 ? `${money(Math.round(v.betragCents / v.gaeste))} pro Gast` : null;
}

/**
 * Die Varianten als Radio-Karten (Titel, Preis pro Gast, Gästezahl, Betrag). Gemeinsam genutzt vom
 * Rückfrage-Dialog oben und vom Bezahlt-Dialog (`BezahltDialog.tsx`), damit beide gleich aussehen.
 * Der Betrag ist reine Anzeige - gebucht wird der Betrag aus der Datenbank.
 */
export function VariantenKarten({
  varianten,
  gewaehlt,
  onWahl,
  name = "variante-waehlen",
}: {
  varianten: VarianteOption[];
  gewaehlt: string | null;
  onWahl: (id: string) => void;
  name?: string;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="sr-only">Variante</legend>
      {varianten.map((v) => {
        const aktiv = gewaehlt === v.id;
        return (
          <label
            key={v.id}
            data-testid={`variante-${v.id}`}
            className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 transition-colors ${
              aktiv ? "border-primary bg-primary/5" : "border-outline-variant/60 hover:bg-surface-container-low dark:border-outline/30"
            }`}
          >
            <Radio name={name} className="mt-0.5" checked={aktiv} onChange={() => onWahl(v.id)} />
            <span className="min-w-0 flex-1">
              <span className="block text-[13.5px] font-semibold text-on-background dark:text-inverse-on-surface">{v.titel}</span>
              <span className="mt-0.5 block text-[12px] text-secondary dark:text-secondary-fixed-dim">
                {[proGast(v), `${v.gaeste} Gäste`].filter(Boolean).join(" · ")}
              </span>
            </span>
            <span className="shrink-0 text-[13.5px] font-semibold tabular-nums text-on-background dark:text-inverse-on-surface">{money(v.betragCents)}</span>
          </label>
        );
      })}
    </fieldset>
  );
}

export function VarianteWaehlenDialog({
  open,
  varianten,
  onWahl,
  onClose,
}: {
  open: boolean;
  varianten: VarianteOption[];
  onWahl: (id: string) => void;
  onClose: () => void;
}) {
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  return (
    <Modal
      open={open}
      onClose={onClose}
      width="520px"
      testId="variante-waehlen-dialog"
      title="Welche Variante hat der Gast gebucht?"
      subtitle="Der Auftragsbetrag und die Zahlung richten sich nach dieser Variante."
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="button" data-testid="variante-uebernehmen" disabled={!gewaehlt} onClick={() => gewaehlt && onWahl(gewaehlt)}>
            Übernehmen
          </Button>
        </>
      }
    >
      <VariantenKarten varianten={varianten} gewaehlt={gewaehlt} onWahl={setGewaehlt} />
    </Modal>
  );
}

/**
 * Führt eine Aktion aus; antwortet der Server mit der Rückfrage `variante_waehlen`, fragt der
 * Dialog und die Aktion läuft ein zweites Mal mit der Wahl. Abbrechen liefert `undefined`.
 * Jeder andere Fehler geht unverändert an den Aufrufer.
 */
export function useVarianteWahl(): {
  mitVarianteWahl: <T>(aktion: (chosenOptionId?: string) => Promise<T>) => Promise<T | undefined>;
  dialog: ReactNode;
} {
  const [varianten, setVarianten] = useState<VarianteOption[] | null>(null);
  const antwort = useRef<((id: string | null) => void) | null>(null);

  const fragen = useCallback(
    (liste: VarianteOption[]) =>
      new Promise<string | null>((resolve) => {
        antwort.current = resolve;
        setVarianten(liste);
      }),
    [],
  );
  const beantworten = useCallback((id: string | null) => {
    antwort.current?.(id);
    antwort.current = null;
    setVarianten(null);
  }, []);

  const mitVarianteWahl = useCallback(
    async <T,>(aktion: (chosenOptionId?: string) => Promise<T>): Promise<T | undefined> => {
      try {
        return await aktion();
      } catch (err) {
        const liste = variantenAusFehler(err);
        if (!liste) throw err;
        const id = await fragen(liste);
        if (!id) return undefined;
        return aktion(id);
      }
    },
    [fragen],
  );

  const dialog: ReactNode = varianten ? (
    <VarianteWaehlenDialog key={varianten.map((v) => v.id).join("|")} open varianten={varianten} onWahl={(id) => beantworten(id)} onClose={() => beantworten(null)} />
  ) : null;
  return { mitVarianteWahl, dialog };
}
